import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { checkPayment } from 'src/models/cart/cart';
import { cancelTerminalAction } from 'src/models/terminal/terminal';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { formatCurrency } from 'src/utils/numberFormatter';
import { titleize } from 'src/utils/stringFormatter';
import * as Yup from 'yup';

import { usePosContext } from '../PosContext';

interface PaymentDialogProps {
  open: boolean;
}

interface NonGuestReceiptOptionsProps {
  onComplete: () => void;
  t: (key: string) => string;
}

function NonGuestReceiptOptions({
  onComplete,
  t,
}: NonGuestReceiptOptionsProps) {
  return (
    <Stack spacing={2} width="100%">
      <Stack direction="row" spacing={2}>
        <Button
          variant="prominent"
          fullWidth
          onClick={() => {
            onComplete();
          }}
        >
          {t('close')}
        </Button>
      </Stack>
    </Stack>
  );
}

export default function ProcessingPaymentDialog({ open }: PaymentDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pos.processingPaymentDialog',
  });

  const [processing, setIsProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const {
    savedCart,
    totalPrice,
    paymentMethod,
    setIsProcessingPayment,
    handleReset,
    isGuestCheckout,
    updateGuestInfo,
    selectedTerminal,
    paymentCreated,
  } = usePosContext();

  const isSwipedCard = paymentMethod?.type === 'card_present';

  const handleCompleteTransaction = () => {
    setIsProcessingPayment(false);
    handleReset();
  };

  const onClose = async () => {
    // if the payment was successful, reset the state and close the dialog
    if (success && !processing && !processingError) {
      // For non-guest checkouts, just complete
      if (!isGuestCheckout) {
        handleCompleteTransaction();
      }
      // For guest checkouts, the form will handle completion
    }
    // If client closes the dialog while ready to swipe card
    else if (processing && !success && !processingError && isSwipedCard) {
      try {
        // Cancel the terminal action
        if (selectedTerminal?.stripeId) {
          await cancelTerminalAction(selectedTerminal.stripeId as string);
        }
      } finally {
        setIsProcessingPayment(false);
      }
    } else {
      setIsProcessingPayment(false);
    }
  };

  useEffect(() => {
    if (!savedCart?.id || !paymentCreated) return;

    let mounted = true;
    let intervalId: NodeJS.Timeout | undefined = undefined;

    const checkStatus = async () => {
      try {
        const data = await checkPayment({
          setup_intent: savedCart.setupIntentId || '',
          setup_intent_secret: savedCart.setupIntentSecret || '',
          payment_intent: savedCart.paymentIntentId || '',
          payment_intent_secret: savedCart.paymentIntentSecret || '',
          cart_id: savedCart.id,
        });

        if (!mounted) return;

        switch (data.status) {
          case 'succeeded':
            clearInterval(intervalId);
            setIsProcessing(false);
            setSuccess(true);
            break;
          case 'incomplete':
            clearInterval(intervalId);
            setIsProcessing(false);
            setSuccess(false);
            setProcessingError(data.error_message);
            break;
          case 'error':
            clearInterval(intervalId);
            setIsProcessing(false);
            setSuccess(false);
            setProcessingError(data.error_message);
            break;
          case 'waiting':
            setIsProcessing(true);
            setSuccess(false);
            setProcessingError(null);
            break;
          default:
            clearInterval(intervalId);
            setIsProcessing(false);
            setSuccess(false);
            setProcessingError(t('unknownPaymentError'));
            break;
        }
      } catch (error) {
        if (!mounted) return;
        clearInterval(intervalId);
        setIsProcessing(false);
        setSuccess(false);
        setProcessingError(t('unknownPaymentError'));
      }
    };

    // Start checking immediately
    checkStatus();
    // Then check every second
    intervalId = setInterval(checkStatus, 1000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [paymentCreated, savedCart, t]);

  return (
    <Dialog
      open={open}
      onClose={paymentCreated ? onClose : undefined}
      fullWidth
      maxWidth="md"
    >
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {processing && (
            <Stack display="flex" alignItems="center" spacing={2}>
              <CircularProgress size="large" />
              <Typography variant="h6">{t('processingPayment')}</Typography>
              <Typography variant="body1" color="text.secondary">
                {t('doNotCloseModal')}
              </Typography>

              {isSwipedCard && paymentCreated && (
                <Button variant="bordered" onClick={onClose} fullWidth>
                  {t('cancelSwipe')}
                </Button>
              )}
            </Stack>
          )}
          {success && (
            <Stack display="flex" alignItems="center" spacing={2} width="100%">
              <Stack alignItems="center" gap={2}>
                <Icon icon={faCheckCircle} size="large" color="success" />
                <Stack alignItems="center" gap={1}>
                  <Typography variant="h4">{t('successTitle')}</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {isGuestCheckout
                      ? t('successThankYouGuest')
                      : t('successThankYouReceipt')}
                  </Typography>
                </Stack>
              </Stack>

              <Stack
                justifyContent="center"
                gap={1}
                bgcolor="background.level2"
                width="100%"
                borderRadius={1}
                padding={2}
                border="1px solid"
                borderColor="divider"
                color="text.secondary"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>{t('successAmountPaid')}:</Typography>
                  <Typography>
                    {formatCurrency(totalPrice, {
                      cents: true,
                      currency: savedCart?.currency || 'usd',
                    })}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>{t('successPaymentMethod')}:</Typography>
                  <Typography>
                    {paymentMethod?.id && paymentMethod.type !== 'card_present'
                      ? `${titleize(paymentMethod.brand)} ${
                          paymentMethod.last4
                        }`
                      : t('swipedCard')}
                  </Typography>
                </Stack>
              </Stack>

              {isGuestCheckout ? (
                <Formik
                  initialValues={{
                    firstName: '',
                    lastName: '',
                    email: '',
                    sendReceipt: false,
                  }}
                  validationSchema={Yup.object({
                    firstName: Yup.string().required(t('firstNameRequired')),
                    lastName: Yup.string().required(t('lastNameRequired')),
                    email: Yup.string()
                      .email(t('emailInvalid'))
                      .required(t('emailRequired')),
                    sendReceipt: Yup.boolean(),
                  })}
                  onSubmit={async (values) => {
                    try {
                      await updateGuestInfo(
                        values.email,
                        values.firstName,
                        values.lastName,
                        values.sendReceipt
                      );
                    } catch (error) {
                      // Error is already handled by the mutation
                    } finally {
                      handleCompleteTransaction();
                    }
                  }}
                >
                  {({ submitForm, values, setFieldValue }) => (
                    <Form style={{ width: '100%' }}>
                      <Stack spacing={3} width="100%">
                        <Divider />

                        <Typography variant="h6" textAlign="center">
                          {t('guestInformation')}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                          >
                            {t('guestInformationDescription')}
                          </Typography>
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormTextField
                              name="firstName"
                              label={t('firstName')}
                              fullWidth
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormTextField
                              name="lastName"
                              label={t('lastName')}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 12 }}>
                            <FormTextField
                              name="email"
                              label={t('email')}
                              type="email"
                              fullWidth
                            />
                          </Grid>
                        </Grid>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                            checked={values.sendReceipt}
                            onChange={(e) =>
                              setFieldValue('sendReceipt', e.target.checked)
                            }
                          />
                          <Typography variant="body2">
                            {t('sendReceipt')}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="bordered"
                            fullWidth
                            onClick={handleCompleteTransaction}
                          >
                            {t('skipAndClose')}
                          </Button>
                          <Button
                            variant="prominent"
                            fullWidth
                            onClick={submitForm}
                          >
                            {t('saveAndClose')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              ) : (
                <NonGuestReceiptOptions
                  onComplete={handleCompleteTransaction}
                  t={t}
                />
              )}
            </Stack>
          )}
          {processingError && (
            <Stack alignItems="center" gap={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <Icon icon={faExclamationTriangle} size="large" color="error" />
                <Typography variant="h6" fontWeight={500}>
                  {processingError}
                </Typography>
              </Box>

              <Button
                variant="bordered"
                color="primary"
                onClick={onClose}
                fullWidth
              >
                {t('close')}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
